/**
 * @fileoverview Dammen Piece Component
 * @description Renders a checkers piece with Opa/Oma character
 */

import React, { memo } from "react";
import { View, StyleSheet } from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { PlayerId, PieceType, CHARACTER_COLORS } from "../types";

interface DammenPieceProps {
  owner: PlayerId;
  type: PieceType;
  size?: number;
  isSelected?: boolean;
  isAnimating?: boolean;
}

/**
 * Character SVG images
 */
const CHARACTER_IMAGES: Record<PlayerId, any> = {
  opa: require("@/assets/games/opa_endless_runner.svg"),
  oma: require("@/assets/games/omam_endless_runner.svg"),
};

/**
 * Dammen Piece Component
 * Displays Opa or Oma character as a checker piece
 */
export const DammenPiece: React.FC<DammenPieceProps> = memo(
  ({ owner, type, size = 40, isSelected = false, isAnimating = false }) => {
    const pieceColor = CHARACTER_COLORS[owner];
    const isKing = type === "king";

    return (
      <View
        style={[
          styles.container,
          {
            width: size,
            height: size,
            backgroundColor: pieceColor,
            borderColor: isSelected ? "#FFD700" : pieceColor,
            borderWidth: isSelected ? 3 : 2,
            shadowColor: isSelected ? "#FFD700" : "#000",
            shadowOpacity: isSelected ? 0.6 : 0.3,
            transform: [{ scale: isAnimating ? 1.1 : 1 }],
          },
        ]}
      >
        {/* Character image */}
        <Image
          source={CHARACTER_IMAGES[owner]}
          style={[styles.characterImage, { width: size * 0.7, height: size * 0.7 }]}
          contentFit="contain"
        />

        {/* King crown indicator */}
        {isKing && (
          <View style={styles.crownContainer}>
            <Ionicons name="star" size={size * 0.3} color="#FFD700" />
          </View>
        )}
      </View>
    );
  }
);

DammenPiece.displayName = "DammenPiece";

const styles = StyleSheet.create({
  container: {
    borderRadius: 100,
    alignItems: "center",
    justifyContent: "center",
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 4,
  },
  characterImage: {
    borderRadius: 100,
  },
  crownContainer: {
    position: "absolute",
    top: -4,
    right: -4,
    backgroundColor: "#1a1a1a",
    borderRadius: 10,
    padding: 2,
  },
});

